import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Withdrawals from "@/pages/Withdrawals";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import Shop from "@/pages/Shop";
import MyOrders from "@/pages/MyOrders";
import Kyc from "@/pages/Kyc";
import Welcome from "@/pages/Welcome";
import AdPortal from "@/pages/AdPortal";
import Inbox from "@/pages/Inbox";
import Affiliate from "@/pages/Affiliate";
import PublicProduct from "@/pages/PublicProduct";
import PublicShell from "@/components/PublicShell";
import Membership from "@/pages/Membership";
import MembershipPurchase from "@/pages/MembershipPurchase";
import HowItWorks from "@/pages/HowItWorks";
import PartnerRewards from "@/pages/PartnerRewards";
import Simulator from "@/pages/Simulator";
import About from "@/pages/About";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import SuccessStories from "@/pages/SuccessStories";

function Shell({ children }) {
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  // Detect Emergent OAuth callback hash synchronously (prevents race condition with AuthProvider).
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public marketing site — the commerce ecosystem story */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/how-it-works" element={<PublicShell><HowItWorks /></PublicShell>} />
      <Route path="/membership" element={<PublicShell><Membership /></PublicShell>} />
      <Route path="/partner-rewards" element={<PublicShell><PartnerRewards /></PublicShell>} />
      <Route path="/simulator" element={<PublicShell><Simulator /></PublicShell>} />
      <Route path="/about" element={<PublicShell><About /></PublicShell>} />
      <Route path="/faq" element={<PublicShell><Faq /></PublicShell>} />
      <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />
      <Route path="/success-stories" element={<PublicShell><SuccessStories /></PublicShell>} />
      <Route path="/p/:productId" element={<PublicShell><PublicProduct /></PublicShell>} />

      {/* Partner-authenticated app */}
      <Route path="/dashboard" element={<ProtectedRoute><Shell><Dashboard /></Shell></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute><Shell><Shop /></Shell></ProtectedRoute>} />
      <Route path="/shop/orders" element={<ProtectedRoute><Shell><MyOrders /></Shell></ProtectedRoute>} />
      <Route path="/earnings" element={<ProtectedRoute><Shell><Withdrawals /></Shell></ProtectedRoute>} />
      <Route path="/rewards" element={<ProtectedRoute><Shell><Affiliate /></Shell></ProtectedRoute>} />
      <Route path="/kyc" element={<ProtectedRoute><Shell><Kyc /></Shell></ProtectedRoute>} />
      <Route path="/membership/purchase" element={<ProtectedRoute><Shell><MembershipPurchase /></Shell></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><Shell><Inbox /></Shell></ProtectedRoute>} />
      <Route path="/marketing" element={<ProtectedRoute><Shell><AdPortal /></Shell></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />

      {/* Admin — same panel, different focus */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><Shell><Admin /></Shell></ProtectedRoute>} />

      {/* Legacy investment-simulation routes → all redirect to dashboard so
          any bookmarks / cached links from the old identity don't 404. */}
      <Route path="/marketplace" element={<Navigate to="/shop" replace />} />
      <Route path="/vault" element={<Navigate to="/dashboard" replace />} />
      <Route path="/deposits" element={<Navigate to="/dashboard" replace />} />
      <Route path="/withdrawals" element={<Navigate to="/earnings" replace />} />
      <Route path="/guild" element={<Navigate to="/partner-rewards" replace />} />
      <Route path="/ledger" element={<Navigate to="/dashboard" replace />} />
      <Route path="/wall" element={<Navigate to="/success-stories" replace />} />
      <Route path="/ad-portal" element={<Navigate to="/marketing" replace />} />
      <Route path="/loyalty" element={<Navigate to="/rewards" replace />} />
      <Route path="/affiliate" element={<Navigate to="/rewards" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
